"use client";
import { usePathname, useSearchParams } from "next/navigation";
import { useState, useRef } from "react";
import {
  useGetProfileQuery,
  useSendVerificationCode,
  useVerifyPut,
} from "@/api/AppApi/services/auth";
import { ApiMsgTypes } from "@/api/AppApi/common-interfaces/IResultStamp";
import ConfirmSucessModal from "./confirm-success/ConfirmSucessModal";
import CodeForm, {
  resendPeriod,
} from "../../auth-common/confirmation/CodeForm";
import AuthRedirectTo from "../../auth-common/AuthRedirectTo";
import { PagesPaths } from "@/utils/functions/pages-paths";
import { useRouter } from "@/app/components/lib/navigation/useRouter";
import { LoginMethodTypes } from "@/api/AppApi/services/company/interfaces/ICompany";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { IsTopWinner } from "@/utils/functions/global-constants";
import { useTranslation } from "@/hooks/localization/useTranslations";
import useEventTrackers from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/useEventTrackers";
import { EventType, IGTagUserData } from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/helpers/interfaces";
import { getFbcCookie, getFbpCookie } from "@/utils/functions/cookieOperations";
import { v4 as uuidv4 } from "uuid";

interface IRouterConfirmQuery {
  confirm: LoginMethodTypes;
  redirectTo: string;
}
export const accountConfirmQuery = (params: IRouterConfirmQuery) =>
  `${PagesPaths.register_confirm}?confirm=${params.confirm}&redirectTo=${params.redirectTo}`;

const AccountConfirm = () => {
  const getCompany = useGetCompanyQuery();
  const company = getCompany?.data?.result;
  const isSchool = company?.theme?.settings?.features.school;

  const getProfile = useGetProfileQuery();
  const { mobile_fields, email } = getProfile?.data?.result ?? {};
  const [counter, setCounter] = useState(resendPeriod);
  const [showConfirmSuccess, setShowConfirmSuccess] = useState(false);
  const [code, setCode] = useState("");
  const [isError, setIsError] = useState(false);
  const t = useTranslation();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { eventTrackers, GTagCompleteRegistrationEventTracher } = useEventTrackers();
  //=============
  const confirmType = searchParams?.get("confirm") as string;
  const byEmail = confirmType === LoginMethodTypes.by_email;
  const dualConfirm = confirmType === LoginMethodTypes.both;
  const redirectTo = searchParams?.get("redirectTo") as string;
  const FBEventIDRef = useRef<string>("");

  const sendVerificationCode = useSendVerificationCode({
    onSuccess: (res) => {
      setCounter(resendPeriod);
      if (res?.message?.type === ApiMsgTypes.success) {
      }
    },
    onError: (_err) => {
      setCounter(resendPeriod);
    },
  });
  const verifyPost = useVerifyPut({
    isEmail: byEmail,
    onSuccess: (res) => {
      if (res?.message?.type === ApiMsgTypes.success) {
        setCounter(0);
        getProfile.refetch();
        const profileUserData: IGTagUserData = {
          name: getProfile?.data?.result?.name ?? undefined,
          email: getProfile?.data?.result?.email ?? undefined,
          phone: getProfile?.data?.result?.mobile ?? undefined,
        };
        const event_id = FBEventIDRef.current;
        GTagCompleteRegistrationEventTracher(profileUserData);
        eventTrackers(
          EventType.complete_registration,
          [],
          {},
          profileUserData,
          event_id,
        );
        if (dualConfirm) {
          setShowConfirmSuccess(true);
        } else {
          router.replace(
            redirectTo.includes(PagesPaths.profile) ||
              redirectTo.includes(PagesPaths.cart)
              ? redirectTo
              : IsTopWinner(company!)
              ? PagesPaths.agentsForms
              : isSchool
              ? PagesPaths.students
              : "/",
          );
        }
      }
    },
    onError: (error) => {
      if (error.response?.status === 400) {
        setIsError(true);
      } else setIsError(true);
    },
  });
  const sendCode = () => {
    const userName = byEmail
      ? email
      : {
          number: mobile_fields?.number ?? "",
          country: mobile_fields?.dial_code_country?.iso2.toUpperCase() ?? "",
        };
    sendVerificationCode.mutate({
      username: userName!,
    });
  };
  const verifyCode = () => {
    setIsError(false);
    const fbc = (getFbcCookie() as string) ?? null;
    const fbp = (getFbpCookie() as string) ?? null;
    const event_id = uuidv4();
    FBEventIDRef.current = event_id;
    verifyPost.mutate({
      body: { token: code },
      params: { fbc, fbp, event_id },
    });
  };

  const title = byEmail ? t.check_your_email : t.check_your_mobile;
  const subtitle = byEmail
    ? t.enter_email_verification_code
    : t.enter_phone_verification_code;
  const isLoading = sendVerificationCode.isLoading || verifyPost.isLoading;
  return (
    <>
      <ConfirmSucessModal
        byEmail={byEmail}
        show={showConfirmSuccess}
        onClose={() => setShowConfirmSuccess(false)}
      />
      <CodeForm
        title={title}
        subtitle={subtitle}
        setCode={setCode}
        sendCode={sendCode}
        verifyCode={verifyCode}
        byEmail={byEmail}
        isLoading={isLoading}
        counter={counter}
        setCounter={setCounter}
        isError={isError}
        redirectTo={
          dualConfirm ? (
            <AuthRedirectTo
              shallow
              text={t.or}
              authLink={{
                text: byEmail ? t.try_with_mobile : t.try_with_email,
                // href: registerConfiermQuery(!byEmail, mobile_json, email),
                href: accountConfirmQuery({
                  confirm: byEmail
                    ? LoginMethodTypes.by_phone
                    : LoginMethodTypes.by_email,
                  redirectTo: pathname!,
                }),
              }}
            />
          ) : undefined
        }
      />
    </>
  );
};

export default AccountConfirm;
